import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import {
  SplitButtonAction,
  SplitButtonGroup,
  SplitButtonTrigger,
} from '@/components/patterns/split-button'
import {
  AvailabilityChangesMonthView,
  type AvailabilityCalendarAnchor,
  type AvailabilityCalendarDateRange,
  type AvailabilityCalendarRangeSelection,
} from '@/components/availability/AvailabilityChangesMonthView'
import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import {
  InfoNotice,
  InfoNoticeAction,
  OrganizationContextNotice,
} from '@/components/patterns/info-notice'
import { SalesOrdersCalendarMonthPicker } from '@/components/orders/SalesOrdersCalendarMonthPicker'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Can } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import {
  availabilityChangeQueries,
  supportsAvailabilityChangesForOrganizationId,
  useAvailabilityChangeCalendar,
  useAvailabilityChanges,
  useCreateAvailabilityChangeMutation,
} from '@/lib/services/availability-changes/availability-changes.query'
import { availabilityChangeSearchSchema } from '@/lib/services/availability-changes/availability-changes.schema'
import type {
  AvailabilityCalendarOccurrence,
  AvailabilityChange,
} from '@/lib/services/availability-changes/availability-changes.types'
import { locationFilters, locationQueries } from '@/lib/services/locations/locations.query'
import {
  getCurrentSalesOrderCalendarMonth,
  getSalesOrderCalendarMonth,
  getSalesOrderCalendarVisibleDateRange,
} from '@/lib/services/sales-orders/sales-order.schema'
import { storeQueries } from '@/lib/services/stores/stores.query'
import {
  DESIGNERS_CHOICE_ONLY_EMPTY_COPY,
  hasAvailableDesignersChoiceProducts,
} from '@/lib/services/stores/stores.utils'
import { cn, getErrorMessage } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import {
  createFileRoute,
  Link,
  notFound,
  useNavigate,
  type RegisteredRouter,
} from '@tanstack/react-router'
import {
  Ban,
  CalendarDays,
  CalendarPlus,
  ChevronDown,
  Clock,
  Flower2,
  MapPin,
  type LucideIcon,
} from 'lucide-react'
import { useMemo, useRef, useState, type ReactElement } from 'react'
import { toast } from 'sonner'
import {
  AvailabilityQuickCreatePopover,
  type AvailabilityQuickCreateMode,
  type AvailabilityQuickCreateSelection,
} from '@/components/availability/AvailabilityChangeEditors'
import { AvailabilityChangeList, AvailabilityChangesHeader } from './-AvailabilityChangeList'
import {
  filterAvailabilityChanges,
  getQuickCreateAvailabilityPayload,
  getChangeDateInputValue,
  getChangeEndDate,
  getChangeIdentifier,
  getChangeStartDate,
  changeHasTemporaryHours,
  type AvailabilityChangeFilter,
  type AvailabilityEffectFlags,
} from '@/lib/services/availability-changes/availability-changes.utils'
import { getLocationName } from '@/lib/services/locations/locations.utils'

const headerShortcutDurationOptions = [
  { days: 3, label: '3 days' },
  { days: 7, label: '7 days' },
  { days: 14, label: '14 days' },
]

const defaultHeaderShortcutDurationDays = 7

/** A header shortcut that schedules its effects store-wide for a chosen number of days. */
interface HeaderShortcut {
  label: string
  confirmTitle: string
  confirmButtonLabel: string
  successMessage: string
  failureMessage: string
  icon: LucideIcon
  effects: AvailabilityEffectFlags
}

const headerShortcuts = {
  pausePickupDelivery: {
    label: 'Pause pickup & delivery',
    confirmTitle: 'Pause pickup & delivery',
    confirmButtonLabel: 'Pause',
    successMessage: 'Pickup and delivery disabled',
    failureMessage: 'Failed to disable pickup and delivery',
    icon: Ban,
    effects: {
      store_closed: false,
      pickup_paused: true,
      delivery_paused: true,
      designers_choice_only: false,
    },
  },
  designersChoiceOnly: {
    label: "Designer's Choice only",
    confirmTitle: "Switch to Designer's Choice only",
    confirmButtonLabel: 'Turn on',
    successMessage: "Designer's Choice-only availability scheduled",
    failureMessage: "Failed to schedule Designer's Choice-only availability",
    icon: Flower2,
    effects: {
      store_closed: false,
      pickup_paused: false,
      delivery_paused: false,
      designers_choice_only: true,
    },
  },
} satisfies Record<string, HeaderShortcut>

function getHeaderShortcutDateRange(durationDays: number) {
  const start = dayjs()
  const end = start.add(durationDays - 1, 'day')

  return {
    endDate: end.format('YYYY-MM-DD'),
    label: `${start.format('MMM D')} - ${end.format('MMM D')}`,
    startDate: start.format('YYYY-MM-DD'),
  }
}

const scheduleDropdownOptions: {
  mode: AvailabilityQuickCreateMode
  label: string
  description: string
  icon: LucideIcon
  iconClassName: string
}[] = [
  {
    mode: 'temporary_hours',
    label: 'Temporary hours',
    description: 'Set different pickup or delivery hours just for the selected dates.',
    icon: Clock,
    iconClassName: 'bg-status-info-background text-status-info-foreground ring-status-info-border',
  },
  {
    mode: 'availability',
    label: 'Close or limit orders',
    description: "Close the store, pause pickup or delivery, or require Designer's Choice.",
    icon: Ban,
    iconClassName:
      'bg-status-warning-background text-status-warning-foreground ring-status-warning-border dark:bg-status-warning-background dark:text-status-warning-foreground dark:ring-status-warning-border',
  },
]

const scheduleDropdownIconClassName =
  'mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset'

/** Global organizations get no availability permissions; the page treats them as false. */
interface AvailabilityChangesLoaderData {
  isGlobalOrg: boolean
  isReadOnly: boolean
  canCreateAvailabilityChanges?: boolean
  canEditAvailabilityChanges?: boolean
  canDeleteAvailabilityChanges?: boolean
}

/** A saved change from the list, or a calendar occurrence of one. */
type EditableAvailabilityChange = AvailabilityChange | AvailabilityCalendarOccurrence

export const Route = createFileRoute('/_authenticated/$orgId/availability/')({
  component: AvailabilityChangesIndex,
  validateSearch: (search) => availabilityChangeSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({ search }),
  loader: ({ context, deps: { search }, params }): AvailabilityChangesLoaderData => {
    if (!context.ability.can('list', 'availability-changes')) throw notFound()

    if (!supportsAvailabilityChangesForOrganizationId(params.orgId)) {
      return { isGlobalOrg: true, isReadOnly: true }
    }

    const month = getSalesOrderCalendarMonth(search)
    const [start = '', end = ''] = getSalesOrderCalendarVisibleDateRange(month).split('to')

    void context.queryClient.prefetchQuery(
      availabilityChangeQueries.calendar({
        organizationId: params.orgId,
        filters: { start, end },
      })
    )
    void context.queryClient.prefetchQuery(
      availabilityChangeQueries.index({
        organizationId: params.orgId,
        filters: { past: false },
      })
    )

    return {
      isGlobalOrg: false,
      isReadOnly: !context.ability.can('edit', 'availability-changes'),
      canCreateAvailabilityChanges: context.ability.can('create', 'availability-changes'),
      canEditAvailabilityChanges: context.ability.can('edit', 'availability-changes'),
      canDeleteAvailabilityChanges: context.ability.can('delete', 'availability-changes'),
    }
  },
})

export function AvailabilityChangesIndex(): ReactElement {
  const { orgId } = Route.useParams()
  const {
    canCreateAvailabilityChanges = false,
    canDeleteAvailabilityChanges = false,
    canEditAvailabilityChanges = false,
  } = Route.useLoaderData()
  const [quickCreateSelection, setQuickCreateSelection] =
    useState<AvailabilityQuickCreateSelection | null>(null)
  const [isQuickCreateOpen, setIsQuickCreateOpen] = useState(false)

  const openAvailabilityChangeCreate = ({
    anchor,
    endDate,
    mode = null,
    presentation = 'popover',
    change = null,
    startDate,
  }: {
    anchor?: AvailabilityCalendarAnchor | null
    endDate?: string | null
    mode?: AvailabilityQuickCreateMode | null
    presentation?: 'popover' | 'sheet'
    change?: EditableAvailabilityChange | null
    startDate?: string | null
  } = {}) => {
    const selectedDate = dayjs(startDate).isValid() ? dayjs(startDate) : dayjs()
    const selectedEndDate = dayjs(endDate).isValid() ? dayjs(endDate) : selectedDate

    setQuickCreateSelection({
      startDate: selectedDate.format('YYYY-MM-DD'),
      endDate: selectedEndDate.format('YYYY-MM-DD'),
      anchor,
      mode,
      presentation,
      change,
    })
    setIsQuickCreateOpen(true)
  }

  const openAvailabilityChangeEditor = (change: EditableAvailabilityChange) => {
    openAvailabilityChangeCreate({
      startDate: getChangeDateInputValue(getChangeStartDate(change)),
      endDate: getChangeDateInputValue(getChangeEndDate(change)),
      mode: changeHasTemporaryHours(change) ? 'temporary_hours' : 'availability',
      presentation: 'sheet',
      change,
      anchor: {
        left: window.innerWidth / 2,
        top: Math.min(window.innerHeight - 160, 160),
      },
    })
  }

  const openAvailabilityChangeCreateFromMenu = (mode: AvailabilityQuickCreateMode) => {
    openAvailabilityChangeCreate({
      mode,
      presentation: 'sheet',
    })
  }

  const openAvailabilityChangeCreateMode = (mode: AvailabilityQuickCreateMode) => {
    setQuickCreateSelection((currentSelection) =>
      currentSelection
        ? {
            ...currentSelection,
            mode,
            presentation: 'sheet',
          }
        : currentSelection
    )
    setIsQuickCreateOpen(true)
  }
  const handleQuickCreateOpenChange = (isOpen: boolean) => {
    setIsQuickCreateOpen(isOpen)

    if (!isOpen) {
      setQuickCreateSelection(null)
    }
  }

  return (
    <>
      <div className="space-y-4">
        <AdminPageHeader
          icon={CalendarDays}
          title="Closures & Availability"
          description="Plan closures, Designer's Choice-only periods, pickup and delivery limits, and temporary hours."
          actions={
            <IfGlobalOrg show={false}>
              {canCreateAvailabilityChanges ? (
                <Can do="create" on="availability-changes">
                  <AvailabilityChangesHeaderActions
                    organizationId={orgId}
                    onSchedule={openAvailabilityChangeCreateFromMenu}
                  />
                </Can>
              ) : null}
            </IfGlobalOrg>
          }
        />

        <IfGlobalOrg
          show={false}
          fallback={
            <OrganizationContextNotice className="mb-4">
              Closures & Availability is organization-specific. Manage availability inside the right
              florist account.
            </OrganizationContextNotice>
          }
        >
          <AvailabilityChangesContent
            orgId={orgId}
            onChangeClick={canEditAvailabilityChanges ? openAvailabilityChangeEditor : undefined}
            onSelectDateRange={
              canCreateAvailabilityChanges ? openAvailabilityChangeCreate : undefined
            }
            selectedRange={isQuickCreateOpen ? quickCreateSelection : null}
          />
        </IfGlobalOrg>
      </div>

      <AvailabilityQuickCreatePopover
        key={
          quickCreateSelection
            ? `${getChangeIdentifier(quickCreateSelection.change) ?? 'new'}-${quickCreateSelection.startDate}-${quickCreateSelection.endDate}-${quickCreateSelection.mode ?? 'choose'}-${quickCreateSelection.presentation ?? 'popover'}`
            : 'closed'
        }
        selection={quickCreateSelection}
        open={isQuickCreateOpen}
        organizationId={orgId}
        canDelete={canDeleteAvailabilityChanges}
        onClose={() => {
          setIsQuickCreateOpen(false)
          setQuickCreateSelection(null)
        }}
        onClosed={() => setQuickCreateSelection(null)}
        onOpenChange={handleQuickCreateOpenChange}
        onModeSelect={openAvailabilityChangeCreateMode}
      />
    </>
  )
}

function AvailabilityHeaderShortcutAction({
  disabled,
  isPending,
  onConfirm,
  shortcut,
  title,
}: {
  disabled: boolean
  isPending: boolean
  /** Resolves to whether the change was scheduled, which closes the confirmation. */
  onConfirm: (durationDays: number) => Promise<boolean>
  shortcut: HeaderShortcut
  title: string
}) {
  const [selectedDurationDays, setSelectedDurationDays] = useState(
    defaultHeaderShortcutDurationDays
  )
  const [confirmOpen, setConfirmOpen] = useState(false)
  const shouldOpenConfirmAfterDurationMenuCloseRef = useRef(false)
  const Icon = shortcut.icon
  const range = getHeaderShortcutDateRange(selectedDurationDays)

  const handleConfirm = async () => {
    const didCreate = await onConfirm(selectedDurationDays)

    if (didCreate) {
      setConfirmOpen(false)
    }
  }

  return (
    <Popover open={confirmOpen} onOpenChange={setConfirmOpen}>
      <SplitButtonGroup title={title}>
        <PopoverTrigger asChild>
          <SplitButtonAction variant="outline" size="header" disabled={disabled}>
            <Icon className="size-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{shortcut.label}</span>
          </SplitButtonAction>
        </PopoverTrigger>

        <DropdownMenu
          onOpenChange={(isOpen) => {
            if (isOpen) {
              setConfirmOpen(false)
            }
          }}
        >
          <DropdownMenuTrigger asChild>
            <SplitButtonTrigger
              variant="outline"
              size="header"
              aria-label={`${shortcut.label} duration`}
              disabled={disabled}
            >
              <ChevronDown className="size-4" />
            </SplitButtonTrigger>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={8}
            size="xs"
            padding="compact"
            surface="panel"
            className="select-none"
            onCloseAutoFocus={(event) => {
              if (!shouldOpenConfirmAfterDurationMenuCloseRef.current) {
                return
              }

              event.preventDefault()
              shouldOpenConfirmAfterDurationMenuCloseRef.current = false
              setConfirmOpen(true)
            }}
          >
            <DropdownMenuLabel variant="eyebrow">How long?</DropdownMenuLabel>
            {headerShortcutDurationOptions.map((option) => (
              <DropdownMenuItem
                key={option.days}
                size="comfortable"
                onSelect={() => {
                  setSelectedDurationDays(option.days)
                  shouldOpenConfirmAfterDurationMenuCloseRef.current = true
                }}
              >
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SplitButtonGroup>

      <PopoverContent
        align="start"
        sideOffset={10}
        collisionPadding={16}
        padding="spacious"
        elevation="strong"
        className="z-[60]"
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <h3 className="text-[0.95rem] leading-5 font-semibold text-foreground">
              {shortcut.confirmTitle}
            </h3>
            <p className="text-sm leading-5 break-words text-muted-foreground">
              For the next{' '}
              <strong className="font-semibold text-foreground">{selectedDurationDays} days</strong>{' '}
              ({range.label}), store-wide. You can resume anytime.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="outline" disabled={isPending} onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button disabled={isPending} onClick={handleConfirm}>
              {isPending ? 'Saving...' : shortcut.confirmButtonLabel}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

function AvailabilityChangesHeaderActions({
  onSchedule,
  organizationId,
}: {
  onSchedule: (mode: AvailabilityQuickCreateMode) => void
  organizationId: number
}) {
  const createMutation = useCreateAvailabilityChangeMutation()
  const { data: locations = [], isPending: locationsArePending = false } = useQuery({
    ...locationQueries.index({
      organizationId,
      filters: locationFilters().get(),
    }),
    enabled: Boolean(organizationId),
  })
  const { data: store } = useQuery({
    ...storeQueries.show({ organizationId }),
    enabled: Boolean(organizationId),
  })
  const activeLocationIds = useMemo(
    () =>
      locations
        .filter((candidate) => candidate?.is_active !== false)
        .map((candidate) => String(candidate.id)),
    [locations]
  )
  const hasActiveLocations = activeLocationIds.length > 0
  const canUseDesignersChoiceOnly = hasAvailableDesignersChoiceProducts(store)
  const isCreating = Boolean(createMutation.isPending)
  const disableShortcutTitle = hasActiveLocations
    ? 'Disable pickup and delivery for the next 7 days'
    : 'Add an active location before scheduling availability changes'
  const designersChoiceShortcutTitle = !hasActiveLocations
    ? 'Add an active location before scheduling availability changes'
    : canUseDesignersChoiceOnly
      ? "Require Designer's Choice arrangements for the next 7 days"
      : DESIGNERS_CHOICE_ONLY_EMPTY_COPY

  if (!locationsArePending && !hasActiveLocations) {
    return null
  }

  const createShortcutChange = async (shortcut: HeaderShortcut, durationDays: number) => {
    if (!hasActiveLocations) {
      toast.error('No active locations available')
      return false
    }

    const { endDate, startDate } = getHeaderShortcutDateRange(durationDays)

    try {
      await createMutation.mutateAsync({
        ...getQuickCreateAvailabilityPayload({
          effects: shortcut.effects,
          organizationId,
          selectedLocationIds: activeLocationIds,
        }),
        start_date: startDate,
        end_date: endDate,
      })

      toast.success(shortcut.successMessage)
      return true
    } catch (error) {
      toast.error(shortcut.failureMessage, {
        description: getErrorMessage(error),
      })
      return false
    }
  }

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <AvailabilityHeaderShortcutAction
        disabled={isCreating || !hasActiveLocations}
        isPending={isCreating}
        shortcut={headerShortcuts.pausePickupDelivery}
        title={disableShortcutTitle}
        onConfirm={(durationDays) =>
          createShortcutChange(headerShortcuts.pausePickupDelivery, durationDays)
        }
      />
      <AvailabilityHeaderShortcutAction
        disabled={isCreating || !hasActiveLocations || !canUseDesignersChoiceOnly}
        isPending={isCreating}
        shortcut={headerShortcuts.designersChoiceOnly}
        title={designersChoiceShortcutTitle}
        onConfirm={(durationDays) =>
          createShortcutChange(headerShortcuts.designersChoiceOnly, durationDays)
        }
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <AdminHeaderActionButton type="button">
            <CalendarPlus />
            Schedule
            <ChevronDown className="size-4 opacity-70" />
          </AdminHeaderActionButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" size="panel" padding="spacious" surface="panel">
          <div className="grid gap-2">
            {scheduleDropdownOptions.map((option) => {
              const Icon = option.icon

              return (
                <DropdownMenuItem
                  key={option.mode}
                  size="descriptive"
                  onSelect={() => onSchedule(option.mode)}
                >
                  <span className={cn(scheduleDropdownIconClassName, option.iconClassName)}>
                    <Icon className="size-4 text-current" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-foreground">
                      {option.label}
                    </span>
                    <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">
                      {option.description}
                    </span>
                  </span>
                </DropdownMenuItem>
              )
            })}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

function NoActiveLocationsNotice({ orgId }: { orgId: number }) {
  return (
    <InfoNotice
      title="No Active Locations"
      tone="warning"
      icon={<MapPin />}
      action={
        <InfoNoticeAction to={`/${orgId}/settings/locations`}>Manage Locations</InfoNoticeAction>
      }
    >
      Enable or create at least one active location before scheduling availability changes.
    </InfoNotice>
  )
}

function AvailabilityChangesContent({
  onChangeClick,
  onSelectDateRange,
  orgId,
  selectedRange,
}: {
  onChangeClick: ((change: EditableAvailabilityChange) => void) | undefined
  onSelectDateRange: ((selection: AvailabilityCalendarRangeSelection) => void) | undefined
  orgId: number
  selectedRange: AvailabilityCalendarDateRange | null
}) {
  const navigate = useNavigate<RegisteredRouter, typeof Route.fullPath>()
  const search = Route.useSearch()
  const layout = search.layout ?? 'calendar'
  const [searchTerm, setSearchTerm] = useState('')
  const [filter, setFilter] = useState<AvailabilityChangeFilter>('all')
  const [locationFilter, setLocationFilter] = useState('all')
  const [showPastChanges, setShowPastChanges] = useState(false)
  const month = getSalesOrderCalendarMonth(search)
  const dateRange = getSalesOrderCalendarVisibleDateRange(month)
  const [start = '', end = ''] = dateRange.split('to')
  const {
    data: calendarChanges = [],
    isFetching: isCalendarFetching,
    isPending: isCalendarPending,
  } = useAvailabilityChangeCalendar({
    organizationId: orgId,
    filters: { start, end },
    enabled: layout === 'calendar' || Boolean(search.date),
  })
  const { data: changes = [], isFetching: isChangesFetching } = useAvailabilityChanges({
    organizationId: orgId,
    filters: showPastChanges ? undefined : { past: false },
  })
  const { data: locations = [], isPending: locationsArePending = false } = useQuery({
    ...locationQueries.index({
      organizationId: orgId,
      filters: locationFilters().get(),
    }),
    enabled: Boolean(orgId),
  })
  const activeLocations = useMemo(
    () => locations.filter((candidate) => candidate?.is_active !== false),
    [locations]
  )
  const showLocationDetails = activeLocations.length > 1
  const locationOptions = useMemo(
    () =>
      activeLocations
        .map((location) => ({
          value: `id:${location.id}`,
          label: getLocationName(location),
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [activeLocations]
  )
  const locationFilterIsAvailable = locationOptions.some(
    (option) => option.value === locationFilter
  )
  const effectiveLocationFilter =
    showLocationDetails && locationFilterIsAvailable ? locationFilter : 'all'
  const listChanges: EditableAvailabilityChange[] = search.date
    ? calendarChanges.filter((change) => changeCoversDate(change, search.date))
    : changes
  const filteredCalendarChanges = useMemo(
    () => filterAvailabilityChanges(calendarChanges, searchTerm, filter, effectiveLocationFilter),
    [calendarChanges, effectiveLocationFilter, filter, searchTerm]
  )
  const filteredListChanges = useMemo(
    () => filterAvailabilityChanges(listChanges, searchTerm, filter, effectiveLocationFilter),
    [effectiveLocationFilter, filter, listChanges, searchTerm]
  )
  const resultCount =
    layout === 'calendar' ? filteredCalendarChanges.length : filteredListChanges.length
  const isFetching =
    layout === 'calendar' ? isCalendarFetching || isCalendarPending : isChangesFetching
  const hasClearableFilters = Boolean(
    searchTerm.trim() ||
    filter !== 'all' ||
    effectiveLocationFilter !== 'all' ||
    showPastChanges ||
    search.date
  )
  const hasCalendarFilters = Boolean(
    searchTerm.trim() || filter !== 'all' || effectiveLocationFilter !== 'all'
  )

  if (!locationsArePending && activeLocations.length === 0) {
    return <NoActiveLocationsNotice orgId={orgId} />
  }

  const handleMonthChange = (nextMonth: string) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        month: nextMonth === getCurrentSalesOrderCalendarMonth() ? undefined : nextMonth,
      }),
    })
  }

  // The view switcher sends a string; the search schema reads it as the route will.
  const handleLayoutChange = (nextLayout: string) => {
    const layout = availabilityChangeSearchSchema.shape.layout.parse(nextLayout)

    void navigate({ search: (prev) => ({ ...prev, layout }) })
  }

  const handleClearFilters = () => {
    setSearchTerm('')
    setFilter('all')
    setLocationFilter('all')
    setShowPastChanges(false)
    void navigate({ search: (prev) => ({ ...prev, date: undefined }) })
  }

  const toolbar = (
    <AvailabilityChangesHeader
      calendarControls={
        layout === 'calendar' ? (
          <SalesOrdersCalendarMonthPicker month={month} onMonthChange={handleMonthChange} />
        ) : null
      }
      filter={filter}
      hasClearableFilters={hasClearableFilters}
      isFetching={isFetching}
      layout={layout}
      locationFilter={effectiveLocationFilter}
      locationOptions={showLocationDetails ? locationOptions : []}
      resultCount={resultCount}
      searchTerm={searchTerm}
      showPastChanges={showPastChanges}
      onClearFilters={handleClearFilters}
      onFilterChange={setFilter}
      onLocationFilterChange={setLocationFilter}
      onLayoutChange={handleLayoutChange}
      onShowPastChangesChange={setShowPastChanges}
      onSearchCommit={setSearchTerm}
    />
  )

  return (
    <>
      {layout === 'calendar' ? (
        <>
          <div className="overflow-hidden rounded-md border border-border bg-card">{toolbar}</div>
          <AvailabilityChangesMonthView
            changes={filteredCalendarChanges}
            dateRange={dateRange}
            month={month}
            hasActiveFilters={hasCalendarFilters}
            isLoading={isCalendarPending}
            onClearFilters={handleClearFilters}
            onSelectDateRange={onSelectDateRange}
            onChangeClick={onChangeClick}
            showLocationDetails={showLocationDetails}
            selectedRange={selectedRange}
          />
        </>
      ) : (
        <AvailabilityChangeList
          onChangeClick={onChangeClick}
          changes={filteredListChanges}
          showLocationSummary={showLocationDetails}
          toolbar={toolbar}
        />
      )}
    </>
  )
}

function changeCoversDate(
  change: AvailabilityCalendarOccurrence,
  date: string | null | undefined
): boolean {
  const selectedDay = getChangeDateInputValue(date)

  if (!selectedDay) {
    return false
  }

  const startDate = getChangeDateInputValue(getChangeStartDate(change))
  const endDate = getChangeDateInputValue(getChangeEndDate(change))
  const startDay = startDate ?? endDate
  const endDay = endDate ?? startDate

  if (!startDay || !endDay) {
    return false
  }

  return selectedDay >= startDay && selectedDay <= endDay
}
